// Main library exports for programmatic usage

export { WorkflowManager } from './core/workflow-manager';
export { TaskAnalyzer } from './core/task-analyzer';
export { KnowledgeBase } from './core/knowledge-base';
export { WorkflowRunner } from './core/workflow-runner';
export { TaskWatcher } from './core/task-watcher';
export { TaskStore } from './core/task-store';
export { PRDGenerator } from './core/prd-generator';
export { DesignDocManager } from './core/design-doc-manager';
export { HookManager, HookRegistry, HookType } from './core/hooks';
export { AgentManager, type Agent, type AgentExecutionContext, type AgentExecutionResult } from './core/agent-manager';
export { 
  AgentCollaborationManager, 
  COLLABORATION_STRATEGIES,
  type CollaborationTask,
  type CollaborationAgent,
  type CollaborationResult,
  type CollaborationStrategy
} from './core/agent-collaboration';
export { BUILTIN_AGENTS, getBuiltinAgents, getBuiltinAgent, getAgentsByCategory, getAgentsByCapability } from './core/builtin-agents';
export { WorkflowVisualizer, type VisualizationOptions } from './core/workflow-visualizer';
export { TaskMigration, type MigrationResult } from './core/task-migration';

// EARS exports
export { 
  EARSParser, 
  EARSGenerator, 
  EARSTemplateGenerator,
  EARSTaskConverter,
  type EARSRequirement,
  type EARSType,
  type ConversionOptions
} from './core/ears';

// New core exports
export { ConfigManager } from './core/config-manager';
export { ServiceRegistry } from './core/service-registry';
export { EnhancedTaskAnalyzer } from './core/enhanced-task-analyzer';
export { WorkflowIntegration } from './core/workflow-integration';

// Workflow exports
export { WorkflowEngine } from './core/workflow/workflow-engine';
export { WorkflowTemplateLibrary, WorkflowTemplateComposer } from './core/workflow/workflow-templates';
export { WorkflowTriggerManager, TriggerType, type WorkflowTrigger, type TriggerContext } from './core/workflow/workflow-trigger-manager';
export { getBuiltinTriggers, getTriggersByType, getEnabledTriggers } from './core/workflow/builtin-triggers';

// Context exports
export { ContextBuilder } from './core/context/context-builder';
export { ContextOptimizer } from './core/context/context-optimizer';
export { ContextCompressor } from './core/context/context-compressor';
export { DynamicToolCaller } from './core/context/tool-caller';
export { 
  MemoryIntegration, 
  type MemoryIntegrationConfig,
  createContextBuilder,
  createMemoryEnhancedContextBuilder,
  createBasicContextBuilder,
  type CreateContextBuilderOptions
} from './core/context';

// Storage exports
export * from './core/storage';

// Types
export type {
  WorkflowConfig,
  Task,
  TaskPriority,
  TaskStatus,
  Workflow,
  WorkflowPhase,
  KnowledgeEntry,
  KnowledgeType,
  AnalysisResult,
  Recommendation
} from './types';

// Extended Task Types
export type {
  ExtendedTask,
  AgentAssignment,
  PromptTemplateConfig,
  TaskArtifacts,
  TaskRelationType,
  TaskRelation,
  TaskExecutionContext
} from './types/extended-task';
export { isExtendedTask, toExtendedTask } from './types/extended-task';

// New types exports
export type {
  Context,
  ContextMetadata,
  KnowledgeItem,
  ToolContext,
  WorkflowContext,
  HistoryItem,
  OptimizationOptions,
  OptimizationResult,
  CompressionOptions,
  CompressionResult
} from './core/context/types';

export type {
  WorkflowStatus,
  StepStatus,
  StepType,
  WorkflowStep,
  WorkflowAction,
  WorkflowCondition,
  WorkflowContext as WorkflowEngineContext,
  WorkflowEvent,
  WorkflowDefinition,
  WorkflowTrigger,
  WorkflowInstance,
  WorkflowExecutionOptions
} from './core/workflow/workflow-engine';

export type {
  TemplateParameter,
  WorkflowTemplate,
  TemplateCategory
} from './core/workflow/workflow-templates';

// Utils
export { logger } from './utils/logger';
export { parseMarkdown, generateMarkdown } from './utils/markdown';
export { loadConfig, saveConfig } from './utils/config';
export { SimpleCache } from './utils/cache';
export { RipgrepSearch } from './utils/ripgrep';
export { withErrorHandling, withTimeout } from './utils/error-handler';

// Templates
export { WorkflowTemplates } from './templates';

// Constants
export * from './constants';